import { useEffect, useState } from "react";
import axios from "axios";
import trashIcon from "../../assets/images/trash.circle.fill.png";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { renderAsync } from 'docx-preview';  // Import docx-preview
import CustomDropdown from "../../CustomControls/dropdown";
import { Endpoints } from "../../Global/Endpoints";
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from "pdfjs-dist"; // For PDF processing
import mammoth from 'mammoth'; // For Word document processing
import ErrorModal from "../Security/ErrorModal";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const Treatment_Upload_Documents = () => {
    const { t } = useTranslation();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documentType, setDocumentType] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const contract = useSelector((state) => state.userData);
    const memberID = contract.userData.MemberID;
    const token = localStorage.getItem('AccessToken');
    const [loading, setLoading] = useState(false);
    const ssoData = useSelector((state) => state.sso.ssoUserData);
    const isHighmarkPortal = ssoData?.Portal === "Highmark";
    const [isDocsUploading, setIsDocUploading] = useState(false);
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(false);
    const [modalMessage, setModalMessage] = useState("");

    const getPageCount = (file) => {
        return new Promise(async (resolve, reject) => {
            const bytes = await file.arrayBuffer()
            
            const loadingTask = pdfjsLib.getDocument({
                data: bytes,
                disableWorker: true});
                try {
                const pdf = await loadingTask.promise;
                const pages = pdf.numPages;
                resolve(pages);
                } catch (error) {
                reject(error);
            }
        });
    };
   
    useEffect(() => {
        const fetchDocumentTypes = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    Endpoints.GetDocumentType,
                    {
                        params: { _Params: JSON.stringify({ EmailID: contract.userData.EmailID, DeviceType:isHighmarkPortal ? "HighmarkPortal" : "PatientPortal"}) },
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                            "Authorization": `Bearer ${token}`,
                        },
                    }
                );
                setDocumentTypes(response.data || []);
                console.log(response.data)
            } catch (error) {
                console.error(t('errorFetchingDocuments'), error);
                toast.error(t('failedtoloaddocuments'));
            } finally {
                setLoading(false);
            }
        };

        fetchDocumentTypes();
    }, [token]);

    const handleFileSelection = (event) => {
        const files = Array.from(event.target.files);
        let anyInvalidType = false;
        let oversizedFileName = "";
        let duplicateFound = false;
        const allowedTypes = [
            "application/pdf", 
            "text/plain", 
            "application/msword", 
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document", 
            "image/jpeg", 
            "image/png", 
            "image/gif",
            "image/tiff",
            "image/tif"
        ];

        // Check for duplicates with already selected files
        for (const file of files) {
          const isDuplicate = selectedFiles.some(
            f => f.name === file.name && f.size === file.size
          );
          if (isDuplicate) {
            duplicateFound = true;
            break;
          }
        }

        // Check for duplicates within the new files themselves
        const fileSignatures = new Set();
        for (const file of files) {
          const signature = `${file.name}_${file.size}`;
          if (fileSignatures.has(signature)) {
            duplicateFound = true;
            break;
          }
          fileSignatures.add(signature);
        }

        if (duplicateFound) {
          toast.error("Duplicate file(s) detected. Please select unique files only.");
          event.target.value = null;
          return;
        }

        if((files.length + selectedFiles.length)>4) return toast.error("Maximum 4 files are allowed");
        const validFiles = files.filter(file => {
            const fileType = file.type;
            if (!allowedTypes.includes(fileType)) {
                anyInvalidType = true;
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                oversizedFileName = file.name;
                return false;
            }
            return true;
        });
        if (anyInvalidType) {
            return toast.error("Invalid file type. Only PDF, TIFF, JPEG, BMP, or PNG allowed.");
            
        } else if (oversizedFileName) {
            return toast.error(`The ${oversizedFileName} is too large (over 5MB). Please upload a smaller file.`);
        }
        setSelectedFiles((prevFiles) => [...prevFiles, ...validFiles]);
        setUploadSuccess(false);

        // Clear the file input value to allow re-uploading the same file
        event.target.value = null;
    };

    const handleDelete = (index) => {
        setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const handleViewFile = async (file) => {
        const fileType = file.type;

        if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
            const fileArrayBuffer = await file.arrayBuffer();
            
            // Open a new window
            const newWindow = window.open("", "_blank");
            if (newWindow) {
                // Write basic HTML structure to new window
                newWindow.document.write(`
                    <html>
                        <head>
                            <title>Document Preview</title>
                            <style>body { font-family: Arial, sans-serif; margin: 20px; }</style>
                        </head>
                        <body>
                            <div id="docx-preview"></div>
                        </body>
                    </html>
                `);
                newWindow.document.close();

                // Render the document into the new window
                renderAsync(fileArrayBuffer, newWindow.document.getElementById("docx-preview"));
            }
        } else {
            // For other types, open directly
            const fileURL = URL.createObjectURL(file);
            window.open(fileURL, '_blank');
        }
    };

    const handleSubmit = async () => {
        if (!documentType) {
            toast.error("Please select a document type.");
            return;
        }
    
        try {
            let fileID = 1; // Initialize FileID to start from 1
            const payloads = []; // Array to hold all file payloads
    
            for (const file of selectedFiles) {
                let noOfPages = 1;
    
                // Determine the number of pages based on file type
                try {
                    const fileType = file.type;
    
                    if (fileType === "application/pdf") {
                        noOfPages = await getPageCount(file);
                    } else if (fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
                        const arrayBuffer = await file.arrayBuffer();
                        const content = await mammoth.extractRawText({ arrayBuffer });
                        const wordCount = content.value.split(/\s+/).length;
                        noOfPages = Math.ceil(wordCount / 500);
                    } else if (fileType === "text/plain") {
                        const textContent = await file.text();
                        const lineCount = textContent.split(/\r?\n/).length;
                        noOfPages = Math.ceil(lineCount / 50);
                    }
                } catch (pageError) {
                    console.warn(`Failed to determine page count for ${file.name}:`, pageError);
                }
    
                const fileToBase64 = (file) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result.split(",")[1]);
                        reader.onerror = (error) => reject(error);
                        reader.readAsDataURL(file);
                    });
    
                const base64Data = await fileToBase64(file);
    
                // Create payload for each file
                const filePayload = {
                    FileID: fileID.toString(), // Use the incrementing FileID
                    FilePath: "",
                    FileName: file.name,
                    NoOFPages: noOfPages.toString(),
                    DocTypeDesc: "",
                    inputBytes: base64Data,
                    fileSize: file.size.toString(),
                    memberId: memberID,
                    contract: contract.userData.ContractIdentified,
                    contractCode: "",
                    docTypeDescription: documentType,
                    DeviceType:isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
                    EmailID:contract.userData.EmailID
                };
    
                payloads.push(filePayload);
                fileID++; // Increment FileID for the next file
            }
            const totalFiles = payloads.length;
            setLoading(true);
            setIsDocUploading(true);
            // Send all files in a single request
            const response = await axios.post(
                Endpoints.UploadDocument,
                JSON.stringify(payloads),
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
   
            // Prepare SendToWin payload for all files
            const sendToWinPayload = {
                _Params: JSON.stringify({
                    MemberID: memberID,
                    Contract: contract.userData.ContractIdentified,
                    DoctypeDesc: "Supporting Clinical Documents",
                    DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal", // or "iOS" if applicable
                    EmailID: contract.userData.EmailID,
                    UserName:"MAPP", 
                    GroupID: "",
                    AuthNo: "",
                    ProviderID: "",
                    Activity: "",
                    ReimbursementType: "",
                    TotalAmount: 0,
                    Speciality: "",
                    PhysicianName: "",
                    PhysicianTelNo: "",
                    PhysicianTaxNo: "",
                    ServiceType: documentType,
                    DocumentTypes:"",
                    TotalFiles: totalFiles,
                    Documents: response.data.map((file, index) => ({
                      NoOFPages: payloads[index].NoOFPages,
                      FilePath: file.FilePath,
                      FileID: payloads[index].FileID,
                      FileName: payloads[index].FileName,
                      DocTypeDesc: "",
                    })),
                }),
            };
    
            // Call SendToWin API
            const sendToWinResponse = await axios.post(Endpoints.SendToWin, sendToWinPayload, {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (sendToWinResponse.data?.Result !== undefined && Number(sendToWinResponse.data?.Result) !== 2) {
                setModalMessage(sendToWinResponse.data?.ResultText || "Failed to upload documents. Please try again.");
                setShowModal(true);
                return;
            }

            setUploadSuccess(true);
            setSelectedFiles([]);
            setDocumentType("");
        } catch (error) {
            console.error("Error during document submission:", error);
            toast.error("An error occurred while processing the documents.");
        }
         finally {
        setLoading(false);
        setIsDocUploading(false);
      }
    };
    
    if (loading) {
        return (
            <div className="loading-container">
                <div className="loading mb-2"></div>
                    <h2 className="loading-text">
                    {!isDocsUploading ? (
                        t("loading")
                    ) : (
                        <>
                        Your documents are being securely uploaded.
                        <br />
                        Please wait for a moment.
                        </>
                    )}
                    </h2>            
                </div>
            );
    }

    // Utility to always trigger file input click
    const onBrowse = () => {
        const input = document.getElementById('selectDocuments');
        if (input) {
            input.value = null; // Always reset so change event fires even for same file
            input.click();
        }
    };
    
    
    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <>
            <ToastContainer />
            <ErrorModal show={showModal} message={modalMessage} onClose={() => setShowModal(false)} closeButton={true} />

            {isHighmarkPortal ? 
                <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
                <button className="win-back-btn" onClick={handleBackClick}>
                    <span>
                    <i className="bi bi-chevron-left"></i>
                    </span>
                </button>       
                <h2 className="page-heading">{t('uploaddocuments')}</h2>
                </div>:
                <div className="col-md-12 mt-4 d-flex inner-page-heading-wrapper">
                <button className="win-back-btn" onClick={handleBackClick}>
                    <span>
                    <i className="bi bi-chevron-left"></i>    
                    {t('Back')}        
                    </span>
                </button>       
                <h2 className="page-heading">{t('uploaddocuments')}</h2>
                </div>
            }

            <div className="col-md-12 col-lg-6 mt-md-0 mt-sm-0">
                <div className="uplooad-doocument-details-btns-wrapper">
                    <input
                        type="file"
                        multiple
                        id="selectDocuments"
                        style={{ display: 'none' }}
                        onChange={handleFileSelection}
                    />
                    <div className="mb-3 mt-lg-3 mt-md-0 mt-sm-0">
                        <label htmlFor="documentType" className="form-label win-form-label mandatory">
                            {t('selectdocument')}
                        </label>

                        <CustomDropdown
                            options={documentTypes} // Pass the array of document types
                            selectedOption={documentType} // Currently selected option
                            onChange={(option) => setDocumentType(option)} // Handle option change
                            searchable={true} // Enable search functionality
                            placeholder= {t('selectdocumenttypes')} // Placeholder text
                            isLoading={loading}
                        />
                    </div>
                    <p className="upload-documents-instruction mt-md-3 mt-sm-0 mb-md-5 mb-sm-0">
                        {t('additionalsupporting')}
                    </p>
                    <p className="upload-documents-instruction text-gray mt-md-0 mt-sm-3 mb-md-5 mb-sm-3">
                        {t('filesallowed')}
                    </p>
                    <button
                        type="button"
                        className="win-btn-secondary mobile-view mt-lg-0 mt-md-0 mt-sm-0"
                        onClick={onBrowse}
                    >
                       {t('attachfiles')}
                    </button>
                </div>                
            </div>

            <div className="col-md-12 col-lg-6 mt-md-5 mt-sm-0">
                {uploadSuccess ? (
                    <div className="documents-uploaded-successfully">
                       {isHighmarkPortal? t('highmarkuploaded'): t('uploadedtowin')}
                    </div>
                ) : selectedFiles.length > 0 ? (
                    <>
                        <div className="documents-grid-wrapper mt-lg-5">
                            {selectedFiles.map((file, index) => (
                                <div className="documents-grid-row" key={index}>
                                    <div className="file-name-column">{file.name}</div>
                                    <div className="file-action-column ms-auto">
                                        <button
                                            className="view-document-icon"
                                            onClick={() => handleViewFile(file) }
                                            style={{backgroundColor: 'transparent', border: 'none',padding: 0,cursor:'pointer'
                                             }}
                                        >
                                            <i className="bi bi-eye-fill"></i>
                                        </button>
                                        <img
                                            src={trashIcon}
                                            alt="Delete"
                                            className="delete-document-icon"
                                            onClick={() => handleDelete(index)}
                                            style={{ cursor: "pointer" }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="document-upload-note text-center mt-4">
                            <button onClick={handleSubmit} className="win-btn-primary mobile-view">{t('submit')}</button>
                        </div>
                    </>
                ) : (
                    <div className="no-documents-uploaded">
                        {t('nofiles')}
                    </div>
                )}
            </div>
            {/* {isChatVisible && <ChatBotWindow />} */}
        </>
    );
};

export default Treatment_Upload_Documents;
